/**
 * Torn companies, one copy for the extension (Company tools), the website (Company page) and the server (the daily
 * history kept for a tracked player, the company directory): reading Torn's v1 company answers, the job positions of a
 * company type, Torn's employee effectiveness formula, and one day of a company's figures. Pure, browser-safe.
 *
 * Torn's answers read here (v1):
 *  - /company/?selections=profile[,detailed,employees,stock] — your own company (detailed/employees/stock: the
 *    director's view);
 *  - /company/{typeId}?selections=companies — every company of a type (the directory; public);
 *  - /torn/?selections=companies — every company type with its positions (requirements, gains, special) and products.
 */

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : null);
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);

// ---------------------------------------------------------------- your company

/**
 * From this terms version on, a tracked player's own company is kept day by day for them (the server's daily read with
 * their key; employees' names and figures only in the director's view). Below it, nothing is kept.
 */
export const COMPANY_HISTORY_TERMS = 8;

export interface Employee {
  id: number;
  name: string;
  position: string;
  daysInCompany: number | null;
  wage: number | null;
  manual: number | null;
  intelligence: number | null;
  endurance: number | null;
  effectiveness: number | null;
  /** The addiction part of effectiveness (zero or negative). */
  addiction: number | null;
  /** Torn's own working-stats part of effectiveness, when it says (director's view). */
  workingStats?: number | null;
  lastAction: number | null;
  status: string;
}

export interface Product {
  name: string;
  cost: number;
  rrp: number;
  price: number;
  inStock: number;
  onOrder: number;
  /** Sold in Torn's last day. */
  soldAmount: number;
  soldWorth: number;
}

export interface Company {
  id: number;
  name: string;
  type: number | null;
  rating: number | null;
  /** The director's player id. */
  director?: number | null;
  dailyIncome: number | null;
  weeklyIncome: number | null;
  dailyCustomers: number | null;
  weeklyCustomers?: number | null;
  employeesHired: number | null;
  employeesCapacity: number | null;
  daysOld: number | null;
  /** Director's view (null when the key can't see it). */
  funds: number | null;
  popularity: number | null;
  efficiency: number | null;
  environment: number | null;
  trainsAvailable: number | null;
  advertising: number | null;
  value: number | null;
  storageSpace: number | null;
  employees: Employee[];
  stock: Product[];
}

/** From v1 /company/?selections=profile[,detailed,employees,stock]. */
export function readCompany(body: unknown): Company | null {
  const b = obj(body);
  const p = obj(b?.company);
  const id = num(p?.ID) ?? num(p?.id);
  if (!b || !p || !id) return null;
  const d = obj(b.company_detailed);
  const emp = obj(b.company_employees) ?? obj(p.employees);
  const stock = obj(b.company_stock);
  return {
    id,
    name: str(p.name) ?? `Company ${id}`,
    type: num(p.company_type),
    rating: num(p.rating),
    director: num(p.director),
    dailyIncome: num(p.daily_income),
    weeklyIncome: num(p.weekly_income),
    dailyCustomers: num(p.daily_customers),
    weeklyCustomers: num(p.weekly_customers),
    employeesHired: num(p.employees_hired),
    employeesCapacity: num(p.employees_capacity),
    daysOld: num(p.days_old),
    funds: num(d?.company_funds),
    popularity: num(d?.popularity),
    efficiency: num(d?.efficiency),
    environment: num(d?.environment),
    trainsAvailable: num(d?.trains_available),
    advertising: num(d?.advertising_budget),
    value: num(d?.value),
    storageSpace: num(obj(d?.upgrades)?.storage_space) ?? num(d?.storage_space),
    employees: Object.entries(emp ?? {})
      .map(([k, v]): Employee | null => {
        const e = obj(v);
        const eid = Number(k);
        if (!e || !Number.isInteger(eid)) return null;
        const eff = obj(e.effectiveness);
        return {
          id: eid,
          name: str(e.name) ?? `#${eid}`,
          position: str(e.position) ?? 'Unassigned',
          daysInCompany: num(e.days_in_company),
          wage: num(e.wage),
          manual: num(e.manual_labor),
          intelligence: num(e.intelligence),
          endurance: num(e.endurance),
          effectiveness: num(eff?.total),
          addiction: num(eff?.addiction),
          workingStats: num(eff?.working_stats),
          lastAction: num(obj(e.last_action)?.timestamp),
          status: str(obj(e.status)?.state) ?? 'Okay',
        };
      })
      .filter((e): e is Employee => !!e)
      .sort((a, b) => a.name.localeCompare(b.name)),
    stock: Object.entries(stock ?? {})
      .map(([name, v]): Product | null => {
        const s = obj(v);
        if (!s) return null;
        return { name, cost: num(s.cost) ?? 0, rrp: num(s.rrp) ?? 0, price: num(s.price) ?? 0, inStock: num(s.in_stock) ?? 0, onOrder: num(s.on_order) ?? 0, soldAmount: num(s.sold_amount) ?? 0, soldWorth: num(s.sold_worth) ?? 0 };
      })
      .filter((s): s is Product => !!s),
  };
}

// ---------------------------------------------------------------- positions and effectiveness

export type WorkingStat = 'manual' | 'intelligence' | 'endurance';

export interface Position {
  name: string;
  manRequired: number;
  intRequired: number;
  endRequired: number;
  /** Which working stat it trains most (and so matters most). */
  main: WorkingStat | null;
  /** Working stats a day's work (or a train) gives. */
  manGain?: number;
  intGain?: number;
  endGain?: number;
  /** Torn's special ability of the position ("Cleaner", "Manager"…), if any, and its description. */
  special?: string | null;
  description?: string | null;
}

export interface CompanyType {
  id: number;
  name: string;
  /** Cost to start one. */
  cost: number | null;
  defaultEmployees: number | null;
  positions: Position[];
  /** The products it sells (names). */
  products: string[];
  /** Its job specials: name → what it does and the stars it needs. */
  specials: Array<{ name: string; effect: string | null; cost: number | null; rating: number | null }>;
}

function readPosition(name: string, v: unknown): Position | null {
  const p = obj(v);
  if (!p) return null;
  const gains = { manual: num(p.man_gain) ?? 0, intelligence: num(p.int_gain) ?? 0, endurance: num(p.end_gain) ?? 0 };
  const top = (Object.entries(gains) as Array<[WorkingStat, number]>).sort((a, b) => b[1] - a[1])[0]!;
  return {
    name,
    manRequired: num(p.man_required) ?? 0,
    intRequired: num(p.int_required) ?? 0,
    endRequired: num(p.end_required) ?? 0,
    main: top[1] > 0 ? top[0] : null,
    manGain: gains.manual,
    intGain: gains.intelligence,
    endGain: gains.endurance,
    special: str(p.special_ability),
    description: str(p.description),
  };
}

/** From v1 /torn/?selections=companies: one company type's positions. */
export function readPositions(types: unknown, typeId: number): Position[] {
  const t = obj(obj(obj(types)?.companies)?.[String(typeId)]);
  return Object.entries(obj(t?.positions) ?? {})
    .map(([name, v]) => readPosition(name, v))
    .filter((p): p is Position => !!p);
}

/** From v1 /torn/?selections=companies: every company type, by id. */
export function readCompanyTypes(body: unknown): CompanyType[] {
  return Object.entries(obj(obj(body)?.companies) ?? {})
    .map(([k, v]): CompanyType | null => {
      const t = obj(v);
      const id = num(k);
      if (!t || !id) return null;
      return {
        id,
        name: str(t.name) ?? `Type ${id}`,
        cost: num(t.cost),
        defaultEmployees: num(t.default_employees),
        positions: Object.entries(obj(t.positions) ?? {})
          .map(([name, p]) => readPosition(name, p))
          .filter((p): p is Position => !!p),
        products: Object.keys(obj(t.stock) ?? {}),
        specials: Object.entries(obj(t.specials) ?? {}).map(([name, s]) => ({ name, effect: str(obj(s)?.effect), cost: num(obj(s)?.cost), rating: num(obj(s)?.rating_required) })),
      };
    })
    .filter((t): t is CompanyType => !!t)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The most demanding position an employee meets every requirement of (the more a
 * position asks for, the more it's worth to the company); null when none fits.
 */
export function bestPosition(e: Pick<Employee, 'manual' | 'intelligence' | 'endurance'>, positions: readonly Position[]): Position | null {
  const fits = positions.filter((p) => (e.manual ?? 0) >= p.manRequired && (e.intelligence ?? 0) >= p.intRequired && (e.endurance ?? 0) >= p.endRequired);
  return fits.sort((a, b) => demand(b) - demand(a))[0] ?? null;
}

const demand = (p: Position) => p.manRequired + p.intRequired + p.endRequired;

/**
 * Torn's working-stats effectiveness for one required stat — the official wiki, wiki.torn.com/wiki/Company ("Working
 * Stats"): `FLOOR(MIN(45, (45 / required) * stat) + MAX(0, 5 * LOG(stat / required, 2)))`. 45 at exactly the
 * requirement (so 90 for a position's two required stats), less below it, a little more (5 per doubling) above it.
 * A stat the position doesn't ask for (requirement 0) adds nothing.
 */
export function statEffectiveness(stat: number, required: number): number {
  if (!(required > 0) || !(stat > 0)) return 0;
  return Math.floor(Math.min(45, (45 / required) * stat) + Math.max(0, 5 * Math.log2(stat / required)));
}

export interface WorkingStats {
  manual: number | null;
  intelligence: number | null;
  endurance: number | null;
}

/** The working-stats part of effectiveness in a position: the formula above for each stat it asks for, added up. */
export function workingStatsEffectiveness(e: WorkingStats, p: Position): number {
  return statEffectiveness(e.manual ?? 0, p.manRequired) + statEffectiveness(e.intelligence ?? 0, p.intRequired) + statEffectiveness(e.endurance ?? 0, p.endRequired);
}

/** Meets every requirement of the position. */
export const meetsPosition = (e: WorkingStats, p: Position): boolean => (e.manual ?? 0) >= p.manRequired && (e.intelligence ?? 0) >= p.intRequired && (e.endurance ?? 0) >= p.endRequired;

export interface PositionFit {
  position: Position;
  /** Working-stats effectiveness there (Torn's formula). */
  effectiveness: number;
  meets: boolean;
  /** What each stat still lacks for the requirement (0 when met). */
  short: { manual: number; intelligence: number; endurance: number };
}

/** Every position of the type for these working stats: the ones they meet first (most demanding first), then the rest by effectiveness. */
export function positionFits(e: WorkingStats, positions: readonly Position[]): PositionFit[] {
  return positions
    .filter((p) => demand(p) > 0)
    .map((p) => ({
      position: p,
      effectiveness: workingStatsEffectiveness(e, p),
      meets: meetsPosition(e, p),
      short: { manual: Math.max(0, p.manRequired - (e.manual ?? 0)), intelligence: Math.max(0, p.intRequired - (e.intelligence ?? 0)), endurance: Math.max(0, p.endRequired - (e.endurance ?? 0)) },
    }))
    .sort((a, b) => Number(b.meets) - Number(a.meets) || (a.meets ? demand(b.position) - demand(a.position) : b.effectiveness - a.effectiveness) || a.position.name.localeCompare(b.position.name));
}

/**
 * The best position for an employee: the most demanding one whose requirements they meet (full working-stats
 * effectiveness, and the position worth most to the company); when they meet none, the one where Torn's formula gives
 * them the most. Null when the type has no positions with requirements.
 */
export function bestFit(e: WorkingStats, positions: readonly Position[]): PositionFit | null {
  return positionFits(e, positions)[0] ?? null;
}

// ---------------------------------------------------------------- the directory (every company of a type)

export interface Competitor {
  id: number;
  name: string;
  rating: number | null;
  directorId: number | null;
  employeesHired: number | null;
  employeesCapacity: number | null;
  dailyIncome: number | null;
  dailyCustomers: number | null;
  weeklyIncome: number | null;
  weeklyCustomers: number | null;
  daysOld: number | null;
}

/**
 * The list inside Torn's answer. Torn's v1 company section answers under `company` (the same root its profile uses), so
 * the list of every company of a type sits in `company` — not `companies` (report 175031-d80f: the panel read
 * `companies`, found nothing and said "Torn returned no companies"). Either root, a map by id or an array.
 */
function competitorEntries(body: unknown): Array<[string, unknown]> {
  const b = obj(body);
  for (const root of [b?.companies, b?.company]) {
    if (Array.isArray(root)) return root.map((v) => [String(num(obj(v)?.ID) ?? num(obj(v)?.id) ?? ''), v]);
    const m = obj(root);
    // A map of companies by id (not a single company's profile, whose values are plain figures).
    if (m && Object.values(m).some((v) => obj(v) && (num(obj(v)!.ID) !== null || typeof obj(v)!.name === 'string'))) return Object.entries(m);
  }
  return [];
}

/** Every company of a type (v1 /company/{type}?selections=companies), most weekly income first. */
export function readCompetitors(body: unknown): Competitor[] {
  return competitorEntries(body)
    .map(([k, v]): Competitor | null => {
      const c = obj(v);
      const id = num(c?.ID) ?? num(c?.id) ?? num(k);
      if (!c || !id) return null;
      return {
        id,
        name: str(c.name) ?? `Company ${id}`,
        rating: num(c.rating),
        directorId: num(c.director),
        employeesHired: num(c.employees_hired),
        employeesCapacity: num(c.employees_capacity),
        dailyIncome: num(c.daily_income),
        dailyCustomers: num(c.daily_customers),
        weeklyIncome: num(c.weekly_income),
        weeklyCustomers: num(c.weekly_customers),
        daysOld: num(c.days_old),
      };
    })
    .filter((c): c is Competitor => !!c)
    .sort((a, b) => (b.weeklyIncome ?? -1) - (a.weeklyIncome ?? -1) || (b.rating ?? 0) - (a.rating ?? 0));
}

/** The server's company directory answer (GET /api/company/directory?type=). */
export interface CompanyDirectoryAnswer {
  type: number;
  /** When Torn was asked (ms). */
  at: number;
  list: Competitor[];
}

/** The server's company types answer (GET /api/company/types). */
export interface CompanyTypesAnswer {
  at: number;
  types: CompanyType[];
}

// ---------------------------------------------------------------- a day of your company (history)

/**
 * One day of the player's own company, kept on the server like their stats history (only for them; background tracking
 * once a day, or the website when they open the Company page): the figures Torn shows them, nothing about employees but
 * how many.
 */
export interface CompanyDay {
  /** Torn day (UTC), "YYYY-MM-DD". */
  day: string;
  /** ms */
  at: number;
  companyId: number;
  name: string;
  type: number | null;
  rating: number | null;
  dailyIncome: number | null;
  dailyCustomers: number | null;
  weeklyIncome: number | null;
  weeklyCustomers: number | null;
  employees: number | null;
  capacity: number | null;
  /** The director's view (null otherwise). */
  popularity: number | null;
  efficiency: number | null;
  environment: number | null;
  funds: number | null;
  advertising: number | null;
  value: number | null;
  /** Read with the director's view. */
  director: boolean;
}

export const tornDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export function companyDayOf(c: Company, atMs: number, director: boolean): CompanyDay {
  return {
    day: tornDay(atMs),
    at: atMs,
    companyId: c.id,
    name: c.name,
    type: c.type,
    rating: c.rating,
    dailyIncome: c.dailyIncome,
    dailyCustomers: c.dailyCustomers,
    weeklyIncome: c.weeklyIncome,
    weeklyCustomers: c.weeklyCustomers ?? null,
    employees: c.employees.length || c.employeesHired,
    capacity: c.employeesCapacity,
    popularity: director ? c.popularity : null,
    efficiency: director ? c.efficiency : null,
    environment: director ? c.environment : null,
    funds: director ? c.funds : null,
    advertising: director ? c.advertising : null,
    value: director ? c.value : null,
    director,
  };
}

const DAY_NUMBERS = ['type', 'rating', 'dailyIncome', 'dailyCustomers', 'weeklyIncome', 'weeklyCustomers', 'employees', 'capacity', 'popularity', 'efficiency', 'environment', 'funds', 'advertising', 'value'] as const;

/** A day from outside (a request, the database): only known fields with sane values; null when it isn't one. */
export function cleanCompanyDay(x: unknown, now: number): CompanyDay | null {
  const o = obj(x);
  const companyId = num(o?.companyId);
  const at = num(o?.at);
  if (!o || !companyId || companyId <= 0 || !Number.isInteger(companyId) || !at || at <= 0 || at > now + 60_000) return null;
  const out = { day: tornDay(at), at, companyId, name: (str(o.name) ?? `Company ${companyId}`).slice(0, 60), director: o.director === true } as CompanyDay;
  for (const k of DAY_NUMBERS) {
    const n = num(o[k]);
    (out as unknown as Record<string, number | null>)[k] = n !== null && Math.abs(n) <= 1e15 ? n : null;
  }
  return out;
}
